import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useTranslation, Trans } from 'react-i18next';
import { useState } from 'react';
import useInviteOnboarding from '../../hooks/api/useInviteOnboarding';
import LanguageSelector from '../../pages/SignupPage/components/LanguageSelector';
import PvXLogo from '../../assets/icons/pvx-logo.svg';

interface InviteOnboardingModalProps {
  open: boolean;
  user: any;
  onClose: () => void;
}

const InviteOnboardingModal = ({ open, user, onClose }: InviteOnboardingModalProps) => {
  const { t: translation } = useTranslation();
  const {
    handleContinueOnboarding,
    termsOfServiceAccepted,
    setTermsOfServiceAccepted,
    newsletterOptIn,
    setnewsletterOptIn,
    isLoading,
  } = useInviteOnboarding(user, onClose);

  const [languageSelectorAnchorEl, setLanguageSelectorAnchorEl] = useState<null | HTMLElement>(
    null,
  );

  return (
    <Dialog
      open={open}
      aria-labelledby="invite-onboarding-modal"
      aria-describedby="invite-onboarding-modal"
      sx={{
        '& .MuiDialog-paper': {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '3rem',
          margin: '0px',
        },
        zIndex: 9995,
        '& .MuiPaper-root': {
          borderRadius: '28px',
          width: 'min(80%, 600px)',
          padding: '1.5rem',
        },
      }}
    >
      <DialogTitle
        id="invite-onboarding-modal-title"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1.75rem',
          p: '0px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '0.5rem',
            border: '1px solid #E8EAEB',
            p: '0.5rem',
            borderRadius: '2rem',
            boxShadow: `
              0px 0.86px 2.21px 0px #E8EAEB59,
              0px 2.06px 5.32px 0px #E8EAEB40,
              0px 3.88px 10.02px 0px #E8EAEB35,
              0px 6.92px 17.87px 0px #E8EAEB2D,
              0px 12.95px 33.42px 0px #E8EAEB24,
              0px 31px 80px 0px #E8EAEB19
            `,
          }}
        >
          <img src={PvXLogo} alt="PvX Logo" style={{ width: '12px', height: '12px' }} />
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 700,
              lineHeight: '1rem',
              color: '#162936',
              textAlign: 'center',
            }}
          >
            PvX Lambda
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <Typography
            sx={{
              fontSize: '3rem',
              fontWeight: 600,
              color: '#989CA2',
              textAlign: 'center',
              lineHeight: '2rem',
              letterSpacing: '-6%',
            }}
          >
            {translation('signup.welcome_to_pvx')}
          </Typography>
          <Typography
            sx={{
              fontSize: '3rem',
              fontWeight: 700,
              color: '#162936',
              lineHeight: 1.1,
              letterSpacing: '-6%',
              textAlign: 'center',
            }}
          >
            {translation('signup.pvx_partners')}
          </Typography>
        </Box>
        <LanguageSelector
          anchorEl={languageSelectorAnchorEl}
          setAnchorEl={setLanguageSelectorAnchorEl}
          isMobile={false}
          isModal={true}
        />
      </DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '0rem' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: '8px',
            }}
          >
            <Checkbox
              checked={newsletterOptIn}
              onChange={(e: any) => setnewsletterOptIn(e.target.checked)}
              sx={{
                padding: '0px',
                color: '#737F86',
                '&.Mui-checked': {
                  color: '#2F736E',
                },
                marginTop: '1px',
              }}
            />
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '1rem',
                color: '#2D3E4ACC',
              }}
            >
              {translation('signup.marketing_communications_opt_in')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Checkbox
              checked={termsOfServiceAccepted}
              onChange={(e: any) => setTermsOfServiceAccepted(e.target.checked)}
              sx={{
                padding: '0px',
                color: '#737F86',
                '&.Mui-checked': {
                  color: '#2F736E',
                },
              }}
            />

            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '1rem',
                color: '#2D3E4ACC',
              }}
            >
              <Trans
                i18nKey={'signup.terms_and_privacy'}
                components={{
                  1: (
                    <a
                      href="https://pvxpartners.com/terms-of-use"
                      aria-label="Terms & Conditions"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#162936',
                        textDecoration: 'underline',
                        fontWeight: 700,
                      }}
                    />
                  ),
                  2: (
                    <a
                      href="https://pvxpartners.com/privacy-policy"
                      aria-label="Privacy Policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#162936',
                        textDecoration: 'underline',
                        fontWeight: 700,
                      }}
                    />
                  ),
                }}
              />
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: '0.5rem' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleContinueOnboarding}
            disabled={!termsOfServiceAccepted || isLoading}
            sx={{
              width: 'fit-content',
              height: '48px',
              borderRadius: '100px',
              fontSize: '0.875rem',
              fontWeight: 700,
              lineHeight: '1.375rem',
              paddingX: '2rem',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              textTransform: 'none',
            }}
          >
            {translation('signup.continue_to_dashboard')}
            {isLoading && <CircularProgress size={20} sx={{ color: '#FFFFFF' }} />}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default InviteOnboardingModal;
